import { ActionPayloadValidationError, requireStringField } from "@slot/client-engine";
import type { ReplayPlugin } from "@slot/client-engine";
import type { PlayerSpinPort } from "@slot/game-shell";
import type { PresentationTaskRunner } from "@slot/presentation";
import { FX_SCOPE } from "../scene/fxScope";
import type { HoldBonusPresenter } from "./bonus/HoldBonusPresenter";
import type { PotsPresenter } from "./pots/PotsPresenter";
import {
  ACTIONS,
  handler,
  parseAwardFullBoard,
  parseBonusTrigger,
  parseBoosters,
  parseCollectBoard,
  parseCollectCoins,
  parseDropMultipliers,
  parseRevealMysteries,
  parseUpdatePots,
} from "./wire";

/** `refill_respins { respins }`: LIFE refills the empty meter before the respin it starts (phase `frame_prepare`). */
function parseRefill(type: string, payload: Record<string, unknown>): { respins: number } {
  const respins = requireStringField(type, payload, "respins");
  if (!/^[1-9]\d*$/.test(respins)) throw new ActionPayloadValidationError(type, "respins", `Must be a whole number of at least 1, got "${respins}".`);
  return { respins: Number(respins) };
}

/** Runs one action's presentation in its own window of the title's scope: a slam snaps it, and only it. */
function inFx(runner: PresentationTaskRunner, run: () => Promise<void>): Promise<void> {
  return runner.runInScope(FX_SCOPE, run);
}

/**
 * The pots' plugin, listed before the board's: on an action both present, the pot moves first (MULTI erupts, then its
 * fire streaks fall and its badges pop; LIFE erupts, then its streak refills the meter). The boosters' activation is
 * the board's (`startBonus`): each pot erupts there in turn, its streak and what it lights on the board before the next.
 */
export function createPotsPlugin(pots: PotsPresenter, runner: PresentationTaskRunner): ReplayPlugin {
  return {
    name: "pots",
    actionHandlers: [
      handler(ACTIONS.updatePots, parseUpdatePots, (input) => inFx(runner, () => pots.presentSpinPots(input))),
      handler(ACTIONS.refillRespins, parseRefill, () => inFx(runner, () => pots.refill())),
      handler(ACTIONS.dropMultipliers, parseDropMultipliers, () => inFx(runner, () => pots.erupt("multi"))),
    ],
  };
}

/**
 * The bonus board's plugin: the trigger, the boosters' effects, the meter, the collects, the badges, the end of the bonus.
 * Every respin waits for the player's Spin before it starts (decision 55: the bonus is decided, the player paces it).
 */
export function createHoldBonusPlugin(bonus: HoldBonusPresenter, runner: PresentationTaskRunner, playerSpins: PlayerSpinPort): ReplayPlugin {
  return {
    name: "hold-bonus",
    actionHandlers: [
      handler(ACTIONS.presentBonusTrigger, parseBonusTrigger, (input) => inFx(runner, () => bonus.presentTrigger(input))),
      handler(ACTIONS.activateBoosters, parseBoosters, (input) => inFx(runner, () => bonus.startBonus(input))),
      handler(ACTIONS.refillRespins, parseRefill, (input) => inFx(runner, () => bonus.refillMeter(input.respins))),
      handler(ACTIONS.collectCoins, parseCollectCoins, (input) => inFx(runner, () => bonus.collect(input))),
      handler(ACTIONS.dropMultipliers, parseDropMultipliers, (input) => inFx(runner, () => bonus.dropMultipliers(input))),
      handler(ACTIONS.revealMysteries, parseRevealMysteries, (input) => inFx(runner, () => bonus.revealMysteries(input))),
      handler(ACTIONS.awardFullBoard, parseAwardFullBoard, (input) => inFx(runner, () => bonus.awardFullBoard(input))),
      handler(ACTIONS.collectBoard, parseCollectBoard, (input) => inFx(runner, () => bonus.collectBoard(input))),
    ],
    observer: {
      // Before anything of a respin: the meter drops and its cells roll only once the player has pressed Spin.
      onEventStart: async (event) => {
        if (event.eventTypeName === "respin") await playerSpins.awaitSpin();
      },
      onFeatureState: async (event) => {
        const state = event.eventContent.featureState;
        if (state) await inFx(runner, () => bonus.onFeatureState(state));
      },
    },
  };
}
